'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, Spinner } from '@common/components';
import { Lottie } from '@common/components/atom/Lottie';
import { cn } from '@core/utils';
import { useSuspenseInfiniteQuery } from '@tanstack/react-query';
import { Fragment, Suspense, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useInView } from 'react-intersection-observer';
import { AuthSdk, UnauthSdk } from '@/api';
import { HomeFeedNativeAd } from '@/app/_components/ads';
import { CustomLink } from '@/app/_components/CustomLink';
import { ROUTES } from '@/constants';
import { useAuthStore } from '@/stores/AuthStore';
import { isAdSlotPosition } from '@/utils/adSlotPosition';
import { ErrorFallback } from './ErrorFallback';
import { FeedCard, type FeedItem } from './FeedCard';
import { extractSeenIds, getInitialSeenIds, loadSeenFeedIds, mergeSeenIds, saveSeenFeedIds, type SeenFeedIds, toBodyFilter } from './feedSeenIds';
import styles from './FeedSkeleton.module.scss';
import { HomeFeedListAdBanner } from './HomeFeedListAdBanner';
const FEED_SIZE = 3;
const SEEN_FEED_IDS_KEY = 'wello-feed-seen-ids';
type FeedPageWithContents = {
  context?: {
    contents?: readonly FeedItem[];
  } | null;
};
const getPageContents = (page: FeedPageWithContents): FeedItem[] => [...(page.context?.contents ?? [])];
function createFeedScopeKey({
  isLogin,
  codeRegion,
  codeSubRegion
}: FeedCardsProps & {
  isLogin: boolean;
}): string {
  return ['homeFeed', isLogin ? 'auth' : 'guest', codeRegion ?? '', codeSubRegion ?? ''].join(':');
}
function FeedSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className={cn(styles.skeleton, "h-dvh xl:overflow-hidden xl:rounded-medium xl:border xl:border-Gray-200")} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function FeedEndMessage() {
  const $ = _c(10);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      triggerOnce: true
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    ref,
    inView
  } = useInView(t0);
  let t1;
  if ($[1] !== inView) {
    t1 = inView && <Lottie loop={false} src="/assets/jsons/check.json" className="size-56" />;
    $[1] = inView;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <div className="size-56">{t1}</div>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="flex flex-col items-center gap-4"><p className="font-600 text-Gray-900 typo-body2">모든 피드를 확인했어요</p><p className="font-400 text-Gray-700 typo-body4">새로운 소식이 올라오면 다시 알려드릴게요</p></div>;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Button asChild={true} theme="sub-line" className="mt-4 bg-Gray-white" dimensions="H32"><CustomLink href={ROUTES.RECOMMEND_POLICY_LIST.pathname}>나의 정책 보러 가기</CustomLink></Button>;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== ref || $[8] !== t2) {
    t5 = <div ref={ref} className="flex flex-col items-center gap-12 py-48">{t2}{t3}{t4}</div>;
    $[7] = ref;
    $[8] = t2;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}
interface FeedCardsProps {
  codeRegion?: string;
  codeSubRegion?: string;
}
function FeedCards(t0) {
  const $ = _c(64);
  const {
    codeRegion,
    codeSubRegion
  } = t0;
  const isLogin = useAuthStore(_temp);
  const [nextPageError, setNextPageError] = useState(false);
  let t1;
  if ($[0] !== codeRegion || $[1] !== codeSubRegion || $[2] !== isLogin) {
    t1 = () => getInitialSeenIds(createFeedScopeKey({
      isLogin,
      codeRegion,
      codeSubRegion
    }), SEEN_FEED_IDS_KEY);
    $[0] = codeRegion;
    $[1] = codeSubRegion;
    $[2] = isLogin;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const [initialSeenIds] = useState(t1);
  let buildBody;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[4] !== codeRegion || $[5] !== codeSubRegion || $[6] !== initialSeenIds || $[7] !== isLogin) {
    const regionQuery = {
      ...(codeRegion && {
        regionCode: codeRegion
      }),
      ...(codeSubRegion && {
        subRegionCode: codeSubRegion
      })
    };
    buildBody = (page, seenIds) => ({
      page,
      size: FEED_SIZE,
      ...regionQuery,
      ...toBodyFilter(seenIds)
    });
    t6 = useSuspenseInfiniteQuery;
    const t7 = isLogin ? "auth" : "guest";
    const t8 = codeRegion ?? "";
    const t9 = codeSubRegion ?? "";
    let t10;
    if ($[14] !== initialSeenIds || $[15] !== t7 || $[16] !== t8 || $[17] !== t9) {
      t10 = ["feed", t7, t8, t9, initialSeenIds];
      $[14] = initialSeenIds;
      $[15] = t7;
      $[16] = t8;
      $[17] = t9;
      $[18] = t10;
    } else {
      t10 = $[18];
    }
    t2 = t10 as const;
    if ($[19] !== isLogin) {
      t3 = async t11 => {
        const {
          pageParam,
          signal
        } = t11;
        const sdk = isLogin ? AuthSdk.getFeedList : UnauthSdk.findFeeds;
        const {
          data
        } = await sdk({
          body: pageParam,
          signal,
          throwOnError: true
        });
        return data;
      };
      $[19] = isLogin;
      $[20] = t3;
    } else {
      t3 = $[20];
    }
    t4 = 600000;
    t5 = buildBody(1, initialSeenIds);
    $[4] = codeRegion;
    $[5] = codeSubRegion;
    $[6] = initialSeenIds;
    $[7] = isLogin;
    $[8] = buildBody;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    buildBody = $[8];
    t2 = $[9];
    t3 = $[10];
    t4 = $[11];
    t5 = $[12];
    t6 = $[13];
  }
  let t7;
  if ($[21] !== buildBody || $[22] !== initialSeenIds) {
    t7 = (t8, allPages) => {
      const {
        context
      } = t8;
      if (!context?.hasNext) {
        return;
      }
      const allContents = allPages.flatMap(getPageContents);
      const seenIds_0 = mergeSeenIds(initialSeenIds, extractSeenIds(allContents));
      return buildBody(allPages.length + 1, seenIds_0);
    };
    $[21] = buildBody;
    $[22] = initialSeenIds;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== t2 || $[25] !== t3 || $[26] !== t4 || $[27] !== t5 || $[28] !== t7) {
    t8 = {
      queryKey: t2,
      queryFn: t3,
      gcTime: t4,
      initialPageParam: t5,
      getNextPageParam: t7
    };
    $[24] = t2;
    $[25] = t3;
    $[26] = t4;
    $[27] = t5;
    $[28] = t7;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  const {
    data: data_0,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = t6(t8);
  let t10;
  let t9;
  if ($[30] !== data_0.pages) {
    t9 = () => {
      const allContents_0 = data_0.pages.flatMap(getPageContents);
      const seenIds_1 = mergeSeenIds(loadSeenFeedIds(SEEN_FEED_IDS_KEY), extractSeenIds(allContents_0));
      saveSeenFeedIds(SEEN_FEED_IDS_KEY, seenIds_1);
    };
    t10 = [data_0.pages];
    $[30] = data_0.pages;
    $[31] = t10;
    $[32] = t9;
  } else {
    t10 = $[31];
    t9 = $[32];
  }
  useEffect(t9, t10);
  let t11;
  if ($[33] !== fetchNextPage) {
    t11 = async () => {
      setNextPageError(false);
      try {
        await fetchNextPage({
          cancelRefetch: false
        });
      } catch {
        setNextPageError(true);
      }
    };
    $[33] = fetchNextPage;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  const handleFetchNextPage = t11;
  let t12;
  if ($[35] !== handleFetchNextPage || $[36] !== hasNextPage || $[37] !== isFetchingNextPage || $[38] !== nextPageError) {
    t12 = {
      rootMargin: "200px 0px",
      onChange: inView => {
        if (inView && hasNextPage && !isFetchingNextPage && !nextPageError) {
          handleFetchNextPage();
        }
      }
    };
    $[35] = handleFetchNextPage;
    $[36] = hasNextPage;
    $[37] = isFetchingNextPage;
    $[38] = nextPageError;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  const {
    ref
  } = useInView(t12);
  const t13 = data_0.pages;
  let hasContents;
  let t14;
  let t15;
  let t16;
  if ($[40] !== data_0.pages) {
    t16 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const flatItems = t13.flatMap(getPageContents);
      hasContents = flatItems.length > 0;
      if (!hasContents) {
        let t17;
        if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
          t17 = <div className="flex items-center justify-center py-64"><p className="font-400 text-Gray-600 typo-body4">아직 피드가 없어요</p></div>;
          $[45] = t17;
        } else {
          t17 = $[45];
        }
        t16 = t17;
        break bb0;
      }
      t14 = "divide-y divide-Gray-200 xl:overflow-hidden xl:rounded-medium xl:border xl:border-Gray-200";
      t15 = flatItems.map((item, itemIndex) => {
        const oneBased = itemIndex + 1;
        const showNative = isAdSlotPosition(oneBased, flatItems.length, {
          firstPosition: 4,
          repeatEvery: 12
        });
        const showBanner = isAdSlotPosition(oneBased, flatItems.length, {
          firstPosition: 10,
          repeatEvery: 12
        });
        return <Fragment key={`feed-${itemIndex}-${item.feedContentType ?? ""}-${item.contentIdx ?? ""}`}>{showNative && <HomeFeedNativeAd />}{showBanner && <HomeFeedListAdBanner />}<FeedCard item={item} priority={itemIndex === 0} /></Fragment>;
      });
    }
    $[40] = data_0.pages;
    $[41] = hasContents;
    $[42] = t14;
    $[43] = t15;
    $[44] = t16;
  } else {
    hasContents = $[41];
    t14 = $[42];
    t15 = $[43];
    t16 = $[44];
  }
  if (t16 !== Symbol.for("react.early_return_sentinel")) {
    return t16;
  }
  let t17;
  if ($[46] !== hasContents || $[47] !== hasNextPage) {
    t17 = !hasNextPage && hasContents && <FeedEndMessage />;
    $[46] = hasContents;
    $[47] = hasNextPage;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  let t18;
  if ($[49] !== handleFetchNextPage || $[50] !== hasNextPage || $[51] !== isFetchingNextPage || $[52] !== nextPageError || $[53] !== ref) {
    t18 = hasNextPage && !nextPageError && <div ref={ref} className="flex items-center justify-center p-16">{isFetchingNextPage ? <Spinner /> : <Button theme="sub-line" dimensions="H32" onClick={handleFetchNextPage}>더 불러오기</Button>}</div>;
    $[49] = handleFetchNextPage;
    $[50] = hasNextPage;
    $[51] = isFetchingNextPage;
    $[52] = nextPageError;
    $[53] = ref;
    $[54] = t18;
  } else {
    t18 = $[54];
  }
  let t19;
  if ($[55] !== handleFetchNextPage || $[56] !== nextPageError) {
    t19 = nextPageError && <div className="flex flex-col items-center gap-12 p-16"><p className="font-400 text-Gray-600 typo-body4">불러오는 중 문제가 발생했습니다.</p><Button theme="sub-line" dimensions="H32" onClick={handleFetchNextPage}>다시 시도</Button></div>;
    $[55] = handleFetchNextPage;
    $[56] = nextPageError;
    $[57] = t19;
  } else {
    t19 = $[57];
  }
  let t20;
  if ($[58] !== t14 || $[59] !== t15 || $[60] !== t17 || $[61] !== t18 || $[62] !== t19) {
    t20 = <div className={t14}>{t15}{t17}{t18}{t19}</div>;
    $[58] = t14;
    $[59] = t15;
    $[60] = t17;
    $[61] = t18;
    $[62] = t19;
    $[63] = t20;
  } else {
    t20 = $[63];
  }
  return t20;
}
function _temp(state) {
  return state.isLogin === true;
}
interface FeedListProps {
  codeRegion?: string;
  codeSubRegion?: string;
}
export const FeedList = t0 => {
  const $ = _c(11);
  const {
    codeRegion,
    codeSubRegion
  } = t0;
  const isLogin = useAuthStore(_temp2);
  if (isLogin === undefined) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <FeedSkeleton />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ErrorFallback />;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <FeedSkeleton />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = isLogin === true;
  let t4;
  if ($[3] !== codeRegion || $[4] !== codeSubRegion || $[5] !== t3) {
    t4 = createFeedScopeKey({
      isLogin: t3,
      codeRegion,
      codeSubRegion
    });
    $[3] = codeRegion;
    $[4] = codeSubRegion;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== codeRegion || $[8] !== codeSubRegion || $[9] !== t4) {
    t5 = <ErrorBoundary fallback={t1}><Suspense fallback={t2}><FeedCards key={t4} codeRegion={codeRegion} codeSubRegion={codeSubRegion} /></Suspense></ErrorBoundary>;
    $[7] = codeRegion;
    $[8] = codeSubRegion;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
};
function _temp2(state) {
  return state.isLogin;
}